import { useContext, useState, useEffect } from 'react';
import { FormNavButtons } from '../../../../components/Additional/FromButtons';
import { Formik } from 'formik';
import { VerticalForm } from '../../../../components/Additional/VerticalForm';
import { FormContext } from '../../../../components/Additional/FormWrapper';
import { validator } from '../../../../components/Additional/validator';
import { FormType } from '../../../../components/Additional/FormType';
import { Input } from "../../../../components/UiComponents/input";
import { Label } from '@radix-ui/react-label';
import CustomSelect from '../../../../components/UiComponents/selectFieldGroup';
import { FileField } from '../../../../components/UiComponents/fileField';
import { downloadResume, extractUsernameFromLinkedInUrl, isLinkedInUrl } from '../../../../helpers/stringHelpers';
import { BasicInformationInitialData, campusOptions, loadFromLocalStorageBasicInfo, pronounsOptions, saveToLocalStorage, semesterOptions, takeStudentDataFromContextBasicInfo, yesNoOptions } from '../Helpers';
import { localStorageKeyBasicInfo } from '../../../../components/LocalStorageProcesser';
import { Heading } from '../../../../components/UiComponents/heading';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { AuthContext } from '../../../../components/AuthWrapper';
import toast from 'react-hot-toast';
import { Button } from '../../../../components/UiComponents/button';
import { FormHeader } from '../FormHeader';
import { Hr } from '../../../../components/Hr';
import { DwDataFromUSFHeadsUp } from './DwDataFromUSFHeadsUp';

export function BasicInfo(): JSX.Element {
  // Getting data from form from Formik and sending it to it
  const { formData, onSubmit, isCreating } = useContext(FormContext);
  const { client, student }: AuthContext = useOutletContext();

  // In case if you have profile but you try to onboard, redirect
  const navigate = useNavigate();
  useEffect(() => {
    if (student && isCreating) {
      navigate(`/profile/${student?.id}`);
    }
  }, [student]);

  const dataFromLocalStorage: BasicInformationInitialData = isCreating ? loadFromLocalStorageBasicInfo() : takeStudentDataFromContextBasicInfo(formData);
  const [formValues] = useState<BasicInformationInitialData>(dataFromLocalStorage);

  // Selected select field (dropdowns) values
  const [selectedPronouns, setSelectedPronouns] = useState<string>(formValues.preferredPronouns);
  const [selectedGraduationTerm, setSelectedGraduationTerm] = useState<string>(formValues.graduationSemester);
  const [selectedIsUndergraduateValue, setSelectedIsUndergraduateValue] = useState<string>(formValues.isUndergraduate);

  return (
    <Formik
      initialValues={formValues}
      onSubmit={(values) => {
        // if (Number(values.entryYear) > Number(values.graduationYear)) {
        //   toast.error("Your entry year cannot be later than your graduation year.")
        //   return;
        // }
        onSubmit(values);
      }}
      validate={validator(FormType.BasicInfo)}
    >
      {({ values, setFieldValue }) => {
        useEffect(() => {
          if (isCreating) {
            saveToLocalStorage(localStorageKeyBasicInfo, values);
          }
        }, [values]);

        // Set the dropdowns data into formik
        useEffect(() => {
          setFieldValue('preferredPronouns', selectedPronouns);
        }, [selectedPronouns]);

        useEffect(() => {
          setFieldValue('graduationSemester', selectedGraduationTerm);
        }, [selectedGraduationTerm]);

        useEffect(() => {
          setFieldValue('isUndergraduate', selectedIsUndergraduateValue);
        }, [selectedIsUndergraduateValue]);

        // Only leaves linkedin name and deletes linkedin link
        useEffect(() => {
          let input = values.linkedinProfile;
          if (isLinkedInUrl(input)) {
            setFieldValue(
              "linkedinProfile",
              extractUsernameFromLinkedInUrl(input)
            );
          }
        }, [values.linkedinProfile]);

        return (
          <VerticalForm>
            <Heading text={isCreating ? "Create Your Student Profile" : "Edit Your Student Profile"} />
            <FormHeader stepTitle="Basic Information (1/2)" />

            <Hr />
            <DwDataFromUSFHeadsUp />
            <Hr />

            <div className="mt-1 space-y-4 w-full">
              <Input
                type="text"
                name="preferredName"
                value={values.preferredName}
                onChange={(e) => setFieldValue('preferredName', e.target.value)}
                className="mt-1 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                title={"Preferred name"}
                required
              />
              <CustomSelect
                label="Preferred Pronouns"
                options={pronounsOptions}
                value={selectedPronouns}
                onChange={setSelectedPronouns}
                required
                name={"preferredPronouns"}
              />
              <CustomSelect
                label="Graduation Semester"
                options={semesterOptions}
                value={selectedGraduationTerm}
                onChange={setSelectedGraduationTerm}
                required
                name={"graduationSemester"}
              />
              <Input
                type="number"
                placeholder='2024'
                name="graduationYear"
                className="mt-1 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                title="Graduation Year"
                required
              />
              <Input
                type="text"
                name="linkedinProfile"
                className="mt-1 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                title="LinkedIn Profile"
              />
              {/* Note: This is currently not working! */}
              {!isCreating && <><div className="font-bold">Resume:</div>
                <Button type={"button"} variant="default" onClick={() => downloadResume(formData.resumeId, client)}>
                  View Your Previous Resume
                </Button>
              </>}
              <FileField
                accept={".pdf,.doc,.docx,.odt,.txt"}
                name="resume"
                fileValue={formData.resume}
                title={isCreating ? "Please upload your resume or CV" : "Please upload your resume or CV, if you want to update it. If you do not want to, just don't do anything."}
                required={false} />
              <div>
                <Label className="block text-md font-medium">Please select any/all student classifications you fall under.</Label>
                <div className="mt-1 space-y-2">
                  <Label className="flex items-center">
                    <Input
                      type="checkbox"
                      name="studentClassification"
                      value="Departmental Honors Program(s)"
                      className="h-4 w-4 text-indigo-600 border-gray-300 rounded"
                    />
                    <span className="ml-2">Departmental Honors Program(s)</span>
                  </Label>
                  <Label className="flex items-center">
                    <Input
                      type="checkbox"
                      name="studentClassification"
                      value="Transfer Student"
                      className="h-4 w-4 text-indigo-600 border-gray-300 rounded"
                    />
                    <span className="ml-2">Transfer Student</span>
                  </Label>
                  <Label className="flex items-center">
                    <Input
                      type="checkbox"
                      name="studentClassification"
                      value="Pell Grant Recipient"
                      className="h-4 w-4 text-indigo-600 border-gray-300 rounded"
                    />
                    <span className="ml-2">Pell Grant Recipient</span>
                  </Label>
                </div>
              </div>
              <div>
                <Label className="block text-md font-medium ">Please select any/all types of scholar programs you are a part of.</Label>
                <div className="mt-1 space-y-2">
                  <Label className="flex items-center">
                    <Input
                      type="checkbox"
                      name="scholarshipPrograms"
                      value="Wilcox Scholars"
                      className="h-4 w-4 text-indigo-600 border-gray-300 rounded"
                    />
                    <span className="ml-2">Wilcox Scholars</span>
                  </Label>
                  <Label className="flex items-center">
                    <Input
                      type="checkbox"
                      name="scholarshipPrograms"
                      value="Kosove Society"
                      className="h-4 w-4 text-indigo-600 border-gray-300 rounded"
                    />
                    <span className="ml-2">Kosove Society</span>
                  </Label>
                  <Label className="flex items-center">
                    <Input
                      type="checkbox"
                      name="scholarshipPrograms"
                      value="GNSI Future Strategists"
                      className="h-4 w-4 text-indigo-600 border-gray-300 rounded"
                    />
                    <span className="ml-2">GNSI Future Strategists</span>
                  </Label>
                  <Label className="flex items-center">
                    <Input
                      type="checkbox"
                      name="scholarshipPrograms"
                      value="Peace Corp Prep"
                      className="h-4 w-4 text-indigo-600 border-gray-300 rounded"
                    />
                    <span className="ml-2">Peace Corps Prep</span>
                  </Label>
                  <Label className="flex items-center">
                    <Input
                      type="checkbox"
                      name="scholarshipPrograms"
                      value="National Merit Scholar"
                      className="h-4 w-4 text-indigo-600 border-gray-300 rounded"
                    />
                    <span className="ml-2">National Merit Scholar</span>
                  </Label>
                </div>
              </div>
            </div>
            <FormNavButtons hideBack />

          </VerticalForm>

        )
      }}
    </Formik>
  );
}
